import { useEffect } from "react";
import { PAGE_SEARCH_ATTR } from "@/components/SearchInput/pageSearch";
import { hasOpenOverlay, hasTransientPopupOpen } from "@/hooks/useOverlayDismiss";
import { shouldHandleAppChord } from "@/lib/keyboardShortcuts";

export { PAGE_SEARCH_ATTR };

function isVisible(element: HTMLElement): boolean {
  let current: HTMLElement | null = element;
  while (current) {
    if (current.hidden) return false;
    const style = window.getComputedStyle(current);
    if (style.display === "none" || style.visibility === "hidden") return false;
    current = current.parentElement;
  }
  return true;
}

export function findPageSearchInput(root: ParentNode = document): HTMLInputElement | null {
  const candidates = root.querySelectorAll<HTMLInputElement>(`input[${PAGE_SEARCH_ATTR}]`);
  for (const input of candidates) {
    if (input.disabled || input.readOnly) continue;
    if (!isVisible(input)) continue;
    return input;
  }
  return null;
}

/** Focus and select the first visible page search input. Returns false when none is ready. */
export function focusPageSearchInput(): boolean {
  const input = findPageSearchInput();
  if (!input) return false;
  input.focus();
  input.select();
  return true;
}

function shouldIgnoreAppShortcut(event: KeyboardEvent): boolean {
  if (!shouldHandleAppChord(event)) return true;
  if (hasOpenOverlay() || hasTransientPopupOpen()) return true;
  if (document.body.dataset.goNavMode === "true") return true;
  return false;
}

function isQuestionMarkKey(event: KeyboardEvent): boolean {
  return event.key === "?" || (event.key === "/" && event.shiftKey);
}

/**
 * `/` focuses the first page SearchInput (`data-page-search`). Suppressed while overlays,
 * menus, or go-nav mode are active. Shift+/ is handled as chat (`?`), not search.
 */
export function useSlashShortcut() {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (shouldIgnoreAppShortcut(event)) return;
      if (event.key !== "/" || event.shiftKey) return;

      if (focusPageSearchInput()) {
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []);
}

/**
 * `?` opens MCP chat when `onOpenMcpPlayground` is provided. Suppressed while overlays,
 * menus, or go-nav mode are active. Keyboard-shortcuts help remains ⌘/Ctrl+Shift+?.
 */
export function useQuestionMarkChatShortcut(onOpenMcpPlayground?: () => void) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!onOpenMcpPlayground) return;
      if (shouldIgnoreAppShortcut(event)) return;
      if (!isQuestionMarkKey(event)) return;

      event.preventDefault();
      onOpenMcpPlayground();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [onOpenMcpPlayground]);
}
